import { useGetAuthMe } from "@birdwatch/client";
import { useUser } from "@clerk/clerk-react";
import { useState } from "react";

import { isCampaignGateOpen } from "./campaign-gate";

/**
 * Whether this member is still inside a campaign gate and must be kept out of the app.
 *
 * One request, and one nobody else makes: the `includes` argument is part of the generated
 * query key, so this is a SECOND `/auth/me` alongside the plain `useGetAuthMe().get()` the
 * role providers share. That is the house pattern for an optional include — `SupportChatIdentify`
 * and `ConnectGoogleCalendarProvider` both pay it — and it is the right trade here: folding
 * the gate into the role query would couple three unrelated concerns onto one cache entry.
 *
 * Fails OPEN. A `/auth/me` error resolves to "not gated", never to a locked app: a platform
 * outage must not shut the whole member base out. Same stance as `usePresaleResume`.
 */
export function useCampaignGate(): { isGated: boolean; isResolving: boolean } {
  const { isLoaded, isSignedIn } = useUser();
  const enabled = isLoaded && Boolean(isSignedIn);

  // Captured once: a `new Date()` in the render body is a fresh object every render, which
  // would let the launch-day gate flip mid-render. Same reason as `usePresaleResume`.
  const [now] = useState(() => new Date());

  const meQuery = useGetAuthMe()
    .query({ includes: ["campaignIntegration"] })
    // `refetchOnWindowFocus` deliberately left ON — the opposite of the `CheckRoleProvider`s.
    // Returning to a tab left open overnight is what re-asks the platform, and it is the only
    // thing that lets launch day unlock the app without a reload.
    .get({ enabled });

  const isGated =
    !meQuery.isError &&
    isCampaignGateOpen(meQuery.data?.campaignIntegration, now);

  // `isLoading`, not `isPending`: a disabled query stays pending forever.
  const isResolving = !meQuery.isError && (!isLoaded || meQuery.isLoading);

  return { isGated, isResolving };
}
